import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AffiliationInvitationStatus } from "@api/shared-runtime/enums";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTRPC } from "@/trpc";
import {
  WorkspaceInvitationEmailEditor,
  type WorkspaceInvitation,
} from "./workspace-invitation-email-editor";
import {
  WORKSPACE_PAGE_SIZE,
  WORKSPACE_SEARCH_DELAY_MS,
  WORKSPACE_MAX_SEARCH_LENGTH,
  lastWorkspaceOffset,
} from "./workspace-search";
import { WorkspacePagination } from "./workspace-pagination";

/** Matches the server's sendable states; accepted/revoked invitations stay read-only. */
function isPendingInvitation(status: AffiliationInvitationStatus): boolean {
  return (
    status === AffiliationInvitationStatus.Draft ||
    status === AffiliationInvitationStatus.Invited ||
    status === AffiliationInvitationStatus.Expired
  );
}

/** Mounted only after the invitation manager is opened. */
export function WorkspaceInvitations({
  organizationBusinessUnitId,
  onClose,
}: {
  organizationBusinessUnitId: string;
  onClose: () => void;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [draftSearch, setDraftSearch] = useState("");
  const [filters, setFilters] = useState({ search: "", offset: 0 });
  const [emailTarget, setEmailTarget] = useState<WorkspaceInvitation | null>(
    null,
  );
  useEffect(() => {
    const timer = setTimeout(
      () =>
        setFilters((previous) =>
          previous.search === draftSearch.trim()
            ? previous
            : { search: draftSearch.trim(), offset: 0 },
        ),
      WORKSPACE_SEARCH_DELAY_MS,
    );
    return () => clearTimeout(timer);
  }, [draftSearch]);
  const query = useQuery(
    trpc.providerWorkspace.listInvitations.queryOptions({
      organizationBusinessUnitId,
      ...filters,
      limit: WORKSPACE_PAGE_SIZE,
    }),
  );
  useEffect(() => {
    if (!query.data || query.isFetching || query.isError) {
      return;
    }
    const lastOffset = lastWorkspaceOffset(query.data.total);
    setFilters((previous) =>
      previous.offset > lastOffset
        ? { ...previous, offset: lastOffset }
        : previous,
    );
  }, [query.data, query.isFetching, query.isError]);
  async function sent() {
    await queryClient.invalidateQueries({
      queryKey: trpc.providerWorkspace.pathKey(),
    });
    toast.success("Invitation sent.");
  }
  const resend = useMutation(
    trpc.organizationBusinessUnitAffiliation.resendInvitation.mutationOptions({
      onSuccess: sent,
      onError: (error) => toast.error(error.message),
    }),
  );
  const pending = resend.isPending;
  return (
    <>
      <Dialog
        open
        onOpenChange={(open) => {
          if (!open && !pending) {
            onClose();
          }
        }}
      >
        <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Provider invitations</DialogTitle>
            <DialogDescription>
              Manage invitations, including people who have not joined the
              active roster.
            </DialogDescription>
          </DialogHeader>
          <Input
            aria-label="Search invitations"
            maxLength={WORKSPACE_MAX_SEARCH_LENGTH}
            placeholder="Search name or email"
            value={draftSearch}
            onChange={(event) => setDraftSearch(event.target.value)}
          />
          <div className="min-h-0 overflow-auto">
            {query.isPending && (
              <p role="status" className="p-6 text-center">
                Loading invitations…
              </p>
            )}
            {query.isError && (
              <div role="alert" className="p-4">
                <p>{query.error.message}</p>
                <Button variant="outline" onClick={() => void query.refetch()}>
                  Try again
                </Button>
              </div>
            )}
            {query.data && !query.isError && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="p-3">Provider</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {query.data.invitations.map((invitation) => (
                    <tr key={invitation.id} className="border-b">
                      <td className="p-3">
                        <p className="font-medium">
                          {[invitation.firstName, invitation.lastName]
                            .filter(Boolean)
                            .join(" ") || "Unnamed provider"}
                        </p>
                        <p className="text-muted-foreground">
                          {invitation.email || "No email address"}
                        </p>
                      </td>
                      <td className="p-3">
                        <Badge variant="outline" className="capitalize">
                          {invitation.status}
                        </Badge>
                      </td>
                      <td className="p-3 text-right">
                        {isPendingInvitation(invitation.status) && (
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={pending}
                              onClick={() => setEmailTarget(invitation)}
                            >
                              {invitation.email ? "Edit email" : "Add email"}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={pending || !invitation.email}
                              onClick={() => {
                                resend.mutate({
                                  organizationBusinessUnitId,
                                  invitationId: invitation.id,
                                });
                              }}
                            >
                              {invitation.status ===
                              AffiliationInvitationStatus.Draft
                                ? "Send invitation"
                                : "Resend"}
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {!query.isError && query.data?.invitations.length === 0 && (
              <p className="p-6 text-center text-muted-foreground">
                No invitations match your search.
              </p>
            )}
          </div>
          {query.data && !query.isError && (
            <WorkspacePagination
              offset={filters.offset}
              total={query.data.total}
              pending={query.isFetching}
              onChange={(offset) =>
                setFilters((previous) => ({ ...previous, offset }))
              }
            />
          )}
        </DialogContent>
      </Dialog>
      {emailTarget && (
        <WorkspaceInvitationEmailEditor
          key={emailTarget.id}
          invitation={emailTarget}
          organizationBusinessUnitId={organizationBusinessUnitId}
          onClose={() => setEmailTarget(null)}
        />
      )}
    </>
  );
}
